import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createIntakeOutput,
  getIntakeOutputById,
  listIntakeOutputByAdmission,
  updateIntakeOutput,
} from "../../services/intakeOutputService";

const MOCK_DOCTORS = [
  { doctorId: 1, doctorName: "Dr Mahesh Mandora" },
  { doctorId: 2, doctorName: "Dr Anita Rao" },
  { doctorId: 3, doctorName: "Dr Prasad Deshmukh" },
];

const initialState = {
  patient: null,
  doctors: MOCK_DOCTORS,
  records: [],
  editingInOutId: null,
  loading: false,
  saving: false,
  error: null,
};

export const fetchIntakeOutputList = createAsyncThunk(
  "intakeOutput/fetchList",
  async (admissionId, { rejectWithValue }) => {
    try {
      return await listIntakeOutputByAdmission(admissionId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchIntakeOutputById = createAsyncThunk(
  "intakeOutput/fetchById",
  async (inOutId, { rejectWithValue }) => {
    try {
      return await getIntakeOutputById(inOutId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveIntakeOutput = createAsyncThunk(
  "intakeOutput/save",
  async ({ inOutId, payload }, { rejectWithValue }) => {
    try {
      if (inOutId) {
        return await updateIntakeOutput(inOutId, payload);
      }
      return await createIntakeOutput(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const intakeOutputSlice = createSlice({
  name: "intakeOutput",
  initialState,
  reducers: {
    setPatient: (state, action) => {
      state.patient = action.payload;
      state.records = [];
      state.editingInOutId = null;
    },
    upsertDoctor: (state, action) => {
      const doctorId = Number(action.payload?.doctorId);
      const doctorName = (action.payload?.doctorName || "").trim();
      if (!Number.isFinite(doctorId) || doctorId <= 0 || !doctorName) return;
      const exists = state.doctors.some((d) => Number(d.doctorId) === doctorId);
      if (!exists) {
        state.doctors.push({ doctorId, doctorName });
      }
    },
    setEditingInOutId: (state, action) => {
      state.editingInOutId = action.payload;
    },
    clearEditing: (state) => {
      state.editingInOutId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntakeOutputList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIntakeOutputList.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload || [];
      })
      .addCase(fetchIntakeOutputList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load Intake & Output records";
        state.records = [];
      })
      .addCase(fetchIntakeOutputById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIntakeOutputById.fulfilled, (state, action) => {
        state.loading = false;
        const row = action.payload;
        if (row?.inOutId) {
          state.editingInOutId = row.inOutId;
        }
        if (row?.doctorId && row?.doctorName) {
          const doctorId = Number(row.doctorId);
          if (
            Number.isFinite(doctorId) &&
            doctorId > 0 &&
            !state.doctors.some((d) => Number(d.doctorId) === doctorId)
          ) {
            state.doctors.push({
              doctorId,
              doctorName: String(row.doctorName).trim(),
            });
          }
        }
      })
      .addCase(fetchIntakeOutputById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load Intake & Output record";
      })
      .addCase(saveIntakeOutput.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveIntakeOutput.fulfilled, (state) => {
        state.saving = false;
        state.editingInOutId = null;
      })
      .addCase(saveIntakeOutput.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save Intake & Output record";
      });
  },
});

export const {
  setPatient,
  upsertDoctor,
  setEditingInOutId,
  clearEditing,
  clearError,
} = intakeOutputSlice.actions;

export default intakeOutputSlice.reducer;
